import { toast } from 'svelte-sonner';

import { dev } from '$app/environment';
import { error } from '@sveltejs/kit';
import { get } from 'svelte/store';

import { localErrorHandlingPolicy, usageUnavailableResponse } from '$lib/api-error-handling';
import { domainErrorKind } from '$lib/api-error-kind';
import { client as apiClient } from '$lib/api/client.gen';
import { versionRequestHeaderValue } from '$lib/build-info';
import maybeDelay from '$lib/dev-delay';
import { isArchivedThreadContentError } from '$lib/editor/thread-content-tree';
import { describeError } from '$lib/error-details';
import {
	refreshUser,
	signOut,
	user as authUser,
	useCredentialsIncludeRequestInterceptor,
} from '$lib/stores/auth';
import errorStore, { AppError } from '$lib/stores/global-error';
import versionPolicy, { parseVersionPolicyHeaderValue } from '$lib/stores/version-policy';

type VersionPolicyHeaderStatus = 'ok' | 'warn' | 'required' | 'policy-unavailable';

function isAbortError(err: unknown, req: Request | undefined): boolean {
	if (req?.signal.aborted) return true;
	if (!err || typeof err !== 'object') return false;

	const errorLike = err as { name?: unknown; code?: unknown };
	return errorLike.name === 'AbortError' || errorLike.code === 'ABORT_ERR';
}

function extractVersionPolicyHeaderStatus(
	response: Response,
): VersionPolicyHeaderStatus | undefined {
	const policyHeader = response.headers.get('X-DS-Version-Policy');
	if (!policyHeader) return undefined;

	return parseVersionPolicyHeaderValue(policyHeader).status;
}

function isHandledVersionPolicyResponse(response: Response, errorKind?: string): boolean {
	const headerStatus = extractVersionPolicyHeaderStatus(response);
	if (headerStatus === 'required' || headerStatus === 'policy-unavailable') {
		return true;
	}

	if (response.status === 426) {
		return true;
	}

	if (response.status === 503 && errorKind === 'VERSION_POLICY_UNAVAILABLE') {
		return true;
	}

	return false;
}

export function init() {
	useCredentialsIncludeRequestInterceptor();

	apiClient.interceptors.request.use((req) => {
		const headers = new Headers(req.headers);
		headers.set('X-DS-Version', versionRequestHeaderValue);
		return new Request(req, { headers });
	});

	apiClient.interceptors.request.use((req, _) => {
		return maybeDelay(Promise.resolve(req));
	});

	apiClient.interceptors.response.use((res) => {
		if (res.ok) {
			versionPolicy.applyResponse(res, undefined, get(authUser)?.id);
		}
		return res;
	});

	apiClient.interceptors.error.use((err, res, req, opts) => {
		const errorKind = domainErrorKind(err);
		const userId = get(authUser)?.id;
		const localErrorPolicy = localErrorHandlingPolicy(req);

		if (!res && isAbortError(err, req)) {
			return err;
		}

		if (res) {
			versionPolicy.applyResponse(res, errorKind, userId);

			if (isHandledVersionPolicyResponse(res, errorKind)) {
				// Let the global version-policy gate own the UX for these states without
				// breaking throwOnError callers that rely on aborted control flow.
				if (opts.throwOnError) {
					const message =
						res.status === 426
							? 'This app version is no longer compatible.'
							: 'Version policy could not be loaded on the server.';
					return error(res.status, message);
				}

				return err;
			}
		}

		if (
			localErrorPolicy?.purpose === 'current-thread-content' &&
			res?.status === 409 &&
			isArchivedThreadContentError(err)
		)
			return err;

		const usageUnavailable = usageUnavailableResponse(req, res, errorKind, err);
		if (usageUnavailable) return usageUnavailable;

		const consoleFn = opts.throwOnError ? console.error : console.warn;
		if (dev) {
			consoleFn(`Error status: ${res?.status}, throwOnError: ${opts.throwOnError}:`, err);
			if (Object.hasOwn(err as any, 'stack')) {
				consoleFn((err as any).stack.toString());
			}
		}

		const requestDescription = req ? `${req.method} ${req.url}` : '(UNKNOWN REQUEST)';

		// Check for errors like network failure where there is no response
		if (!res) {
			if (opts.throwOnError) {
				// Most likely a network error
				if (!localErrorPolicy?.suppressGlobalError) errorStore.addError(err as Error);
				// We still throw in case the caller is following a regular pattern.
				throw error(500, 'Network error occurred');
			} else {
				if (!localErrorPolicy?.suppressNetworkToast) {
					toast.warning(dev ? `Network error: ${requestDescription}` : 'Network error occurred.');
				}
				return err;
			}
		}

		// Check for Redirect responses
		if (
			(res.status >= 300 && res.status < 400) ||
			(res.status === 0 && res.type === 'opaqueredirect')
		) {
			// Just let the caller handle it
			return err;
		}
		if (!res.ok) {
			if (res.status === 401) {
				// Not logged in, do a regular signout that will redirect to login page and clear state
				const { pathname, search, hash } = window.location;
				signOut({ redirectTo: pathname + search + hash });
				return;
			} else if (opts.throwOnError) {
				// The caller expect us to handle the error, so we add to global error store and throw
				const described = describeError(err);
				if (!localErrorPolicy?.suppressGlobalError) {
					errorStore.addError(new AppError(res.status, `API error: ${requestDescription}`, err));
				}
				// We still throw the error in case the caller wants to throw it further
				throw error(res.status, {
					message: described.message || res.statusText,
					request: requestDescription,
					details: described.details,
				} as any);
			} else {
				const described = describeError(err);
				if (!localErrorPolicy?.suppressResponseToast) {
					toast.warning(dev ? described.message : 'There was an error.');
				}
			}
		}
		return err;
	});

	// Refresh user on init if signed in
	refreshUser();
}
